import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
  SUPER_ADMIN_GROUPS,
  SUPER_ADMIN_SECTIONS,
} from "../nav";
import type { SuperAdminSectionId } from "../nav";

/**
 * Operator console sidebar (admin-console-split PR 4). Mirrors the club
 * console's `AdminSidebar` layout/tokens — horizontal snap-scroll on mobile,
 * fixed rail on desktop, active item filled accent via `data-active` (so E2E
 * asserts without scraping className strings). Sections use Lucide icons;
 * there is no per-role filtering here — everything is super-admin-only and
 * the shell gates once.
 */
export function SuperAdminSidebar({ active }: { active: SuperAdminSectionId }) {
  const activeLinkRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    activeLinkRef.current?.scrollIntoView({
      block: "nearest",
      inline: "center",
    });
  }, [active]);

  return (
    <aside
      data-testid="super-admin-sidebar"
      className="w-full shrink-0 border-b border-rule bg-surface px-2 py-2 md:w-56 md:border-b-0 md:border-r md:py-3"
    >
      <nav className="flex snap-x gap-3 overflow-x-auto scroll-px-2 pb-1 md:flex-col md:gap-4 md:overflow-visible md:pb-0">
        {SUPER_ADMIN_GROUPS.map((group) => {
          const items = SUPER_ADMIN_SECTIONS.filter((s) => s.group === group.id);
          if (items.length === 0) return null;

          return (
            <div
              key={group.id}
              className="min-w-max md:min-w-0"
              data-super-admin-group={group.id}
            >
              <h3 className="px-2 mb-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                {group.label}
              </h3>
              <ul className="flex gap-1 md:flex-col md:gap-0.5">
                {items.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.id}>
                      <Link
                        to={s.path}
                        ref={s.id === active ? activeLinkRef : undefined}
                        data-testid={`super-admin-nav-${s.id}`}
                        data-super-admin-section={s.id}
                        data-active={s.id === active ? "true" : undefined}
                        className="group flex min-w-[8.5rem] snap-start items-center gap-2 rounded-control px-2 py-1.5 text-sm text-ink-muted transition-colors hover:bg-field hover:text-ink data-[active=true]:bg-accent data-[active=true]:text-accent-ink md:min-w-0"
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                        <span className="flex flex-col leading-tight">
                          <span className="text-sm">{s.label}</span>
                          <span className="text-[0.6rem] text-current opacity-70">
                            {s.kicker}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
